import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, fmtEUR } from "@/lib/api";
import { X, Printer, Check } from "lucide-react";

export default function PosInvoicePrint({ invoice, onClose }) {
  const [settings, setSettings] = useState({});
  useEffect(() => { api.get("/settings").then(r => setSettings(r.data || {})).catch(() => {}); }, []);

  if (!invoice) return null;
  const dt = new Date(invoice.created_at);
  const payLabel = invoice.payment_method === "cash" ? "Kesh"
    : invoice.payment_method === "card" ? "Kartë"
    : "Faturë";

  return createPortal(
    <div className="pos-print-portal fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3">
      <div className="bg-white text-black max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} style={{color:'#000'}}>
        <div className="flex items-center justify-between p-3 border-b border-neutral-300 no-print bg-neutral-100">
          <div className="flex items-center gap-2 text-emerald-600">
            <Check size={18} />
            <div className="text-sm font-bold uppercase tracking-widest">Fatura u ruajt</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-neutral-200 text-neutral-700" data-testid="pos-inv-close"><X size={18} /></button>
        </div>

        <div className="p-5 pos-invoice-print bg-white text-black" id="invoice-print-area" style={{color:'#000', backgroundColor:'#fff'}}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <img src="/logo-hidrogv.png" alt="HidroGV" className="h-14 w-auto mb-2 pos-invoice-logo" />
              {settings.address && (
                <div className="text-xs text-neutral-700 whitespace-pre-line">{settings.address}</div>
              )}
              {settings.contact_phone && (
                <div className="text-xs text-neutral-700 mt-0.5">Tel: {settings.contact_phone}</div>
              )}
              {settings.email && (
                <div className="text-xs text-neutral-700">Email: {settings.email}</div>
              )}
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-xs uppercase text-neutral-700">Fatura</div>
              <div className="font-mono font-bold text-lg" data-testid="pos-inv-number">{invoice.invoice_number}</div>
              <div className="text-xs text-neutral-600 mt-1">{dt.toLocaleString("sq")}</div>
            </div>
          </div>

          <table className="w-full text-sm mb-4" style={{color:'#000'}}>
            <thead className="text-xs uppercase text-neutral-700 border-b border-neutral-400">
              <tr>
                <th className="text-left py-2 pr-2">Produkti</th>
                <th className="text-right py-2 pr-2">Sasia</th>
                <th className="text-right py-2 pr-2">Çmimi</th>
                <th className="text-right py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((it, i) => (
                <tr key={i} className="border-b border-neutral-200">
                  <td className="py-1.5 pr-2">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-[10px] text-neutral-600 font-mono">{it.article_number || it.sku}</div>
                  </td>
                  <td className="text-right py-1.5 pr-2">{it.qty} {it.unit}</td>
                  <td className="text-right py-1.5 pr-2">{fmtEUR(it.unit_price)}</td>
                  <td className="text-right py-1.5 font-bold">{fmtEUR(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="space-y-1 text-sm border-t border-neutral-400 pt-3">
            <div className="flex justify-between"><span className="text-neutral-700">Nëntotali</span><span>{fmtEUR(invoice.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-700">TVSH</span><span>{fmtEUR(invoice.vat_total)}</span></div>
            {invoice.discount_amount > 0 && (
              <div className="flex justify-between text-emerald-700">
                <span>Zbritje ({invoice.discount_percent}%)</span>
                <span>−{fmtEUR(invoice.discount_amount)}</span>
              </div>
            )}
            {invoice.bonus_amount > 0 && (
              <div className="flex justify-between text-emerald-700">
                <span>Bonus Loyalty ({invoice.bonus_pct}%)</span>
                <span>−{fmtEUR(invoice.bonus_amount)}</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold pt-2 border-t border-neutral-400">
              <span>TOTALI</span>
              <span style={{color:'#000'}}>{fmtEUR(invoice.total)}</span>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between text-xs">
            <span className="text-neutral-700">Mënyra e pagesës:</span>
            <span className="font-bold uppercase">{payLabel}</span>
          </div>
          {invoice.payment_method === "cash" && invoice.cash_given && (
            <>
              <div className="flex items-center justify-between text-xs mt-1">
                <span className="text-neutral-700">Dhënë nga klienti:</span>
                <span>{fmtEUR(invoice.cash_given)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-neutral-700">Kusuri:</span>
                <span className="font-bold">{fmtEUR(invoice.change_returned || 0)}</span>
              </div>
            </>
          )}
          <div className="mt-3 pt-3 border-t border-neutral-400 text-xs text-neutral-700 text-center">
            Shitësi: <span className="font-bold text-black">{invoice.worker_name}</span>
          </div>
          <div className="mt-4 text-center text-[10px] text-neutral-700">
            Faleminderit që zgjodhët HidroGV!
          </div>
        </div>

        <div className="p-3 border-t border-neutral-300 flex gap-2 no-print bg-neutral-100">
          <button onClick={onClose} className="flex-1 py-2.5 border border-neutral-400 uppercase text-xs text-neutral-800 hover:bg-neutral-200">Mbyll</button>
          <button onClick={() => window.print()} data-testid="pos-inv-print"
            className="flex-1 py-2.5 bg-[#FFD600] hover:bg-yellow-400 text-black font-bold uppercase text-xs flex items-center justify-center gap-2">
            <Printer size={14} /> Printo / PDF
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
